import { styled } from '@linaria/react';
import type { ReactNode } from 'react';
import { Icon } from '../../../../../ui-library/icon/icon';

/**
 * The white, bordered, 16px-radius card every Flora settings group sits in.
 */
export const SettingsCard = styled.section`
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 16px;
  overflow: hidden;
`;

/**
 * A card's top row: tile, text, and trailing controls in one line.
 */
export const SettingsCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
`;

/**
 * A full-width, unstyled button that toggles a card or row open; wraps the
 * chevron, tile, and title so the whole header is the click target.
 */
export const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

interface ExpandChevronProps {
  /** Whether its section is open; the chevron then points up. */
  expanded: boolean;
  /** Glyph size in px. */
  size: number;
  color: string;
}

/**
 * Renders the `expand_more` chevron, rotated 180deg while its section is open.
 *
 * The rotation sits on a wrapper because `Icon` sets its own `className`, so a
 * class styled onto `Icon` never reaches the DOM.
 *
 * @param {ExpandChevronProps} props - Open state, glyph size, and colour
 * @returns {JSX.Element} The chevron
 *
 * @example
 * <ExpandChevron expanded={expanded} size={18} color="var(--ink-lighter)" />
 */
export function ExpandChevron({ expanded, size, color }: ExpandChevronProps) {
  return (
    <ChevronSlot data-expanded={expanded || undefined} aria-hidden="true">
      <Icon name="expand_more" size={size} color={color} />
    </ChevronSlot>
  );
}

const ChevronSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  pointer-events: none;
  transition: transform 0.15s;

  &[data-expanded] {
    transform: rotate(180deg);
  }
`;

/**
 * A 32px square, icon-only button for a trailing expand/collapse chevron.
 */
export const ChevronToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--sky-lighter);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

interface SettingsCardTitleProps {
  /** The card heading. */
  title: string;
  /** One line under the heading explaining the setting. */
  description?: ReactNode;
  /** Trailing controls (buttons, counts) aligned to the heading row. */
  actions?: ReactNode;
  /** The heading's id, for `aria-labelledby` on the card. */
  id?: string;
}

/**
 * Renders a settings card's heading block: title, optional description, and
 * optional trailing actions.
 *
 * @param {SettingsCardTitleProps} props - The heading text and actions
 * @returns {JSX.Element} The heading block
 *
 * @example
 * <SettingsCard aria-labelledby="mode-h">
 *   <SettingsCardTitle id="mode-h" title="Default approval mode" description="Applies to new chats." />
 * </SettingsCard>
 */
export function SettingsCardTitle({ title, description, actions, id }: SettingsCardTitleProps) {
  return (
    <TitleRow>
      <TitleText>
        <Heading id={id}>{title}</Heading>
        {description && <Description>{description}</Description>}
      </TitleText>
      {actions && <Actions>{actions}</Actions>}
    </TitleRow>
  );
}

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px 24px 0;
`;

const TitleText = styled.div`
  flex: 1 1 320px;
  min-width: 0;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: var(--font-size-medium);
  line-height: var(--line-height-large);
  font-weight: var(--font-weight-semibold);
  color: var(--ink-darkest);
`;

const Description = styled.p`
  margin: 2px 0 0;
  font-size: var(--font-size-small);
  line-height: 1.25rem;
  color: var(--ink-base);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export type { ExpandChevronProps, SettingsCardTitleProps };
